// Утилиты для работы с Service Worker

import { langSelectors } from '_core/store/Lang';
import { properties } from '_core/utils/component';
import * as http from '_core/utils/http';
import { LANG_TEMPLATE_VAR } from 'basic/config';
import * as api from '_core/utils/api';

function getLangStringFromStore(code, ...vars) {
  try {
    const store = window.__REDUX_STORE__ || (window.store?.getState ? window.store : null);
    if (!store) {
      return code;
    }
    
    const state = typeof store === 'function' ? store() : (store.getState ? store.getState() : store);
    if (!state) {
      return code;
    }
    
    const currentLangId = langSelectors.getCurrentLangId(state);
    if (currentLangId == null) {
      return code;
    }
    
    const langInfo = langSelectors.getLangInfoById(state, currentLangId);
    const langStrings = langSelectors.getLangStringsById(state, currentLangId);
    
    if (langStrings == null) {
      return code;
    }
    
    if (code !== null && typeof code === 'object' && !Array.isArray(code)) {
      return code[langInfo?.code] || code;
    }
    
    let realCode = code;
    let position = 0;
    
    if (Array.isArray(code)) {
      realCode = code[0];
      position = +`${code[1]}`.trim();
      position = Number.isInteger(+position) ? position % 10 : 2;
    }
    
    const reg = /^r\._\./;
    const fixedCode = realCode != null ? `${realCode}`.replace(/^\$/, '').replace(/\?.+$/, '') : '';
    
    let finalCode = fixedCode;
    
    if (reg.test(fixedCode)) {
      finalCode = fixedCode.replace(reg, `r.${process.env.REACT_APP_PROJECT}.`);
      
      if (langStrings[finalCode] == null) {
        finalCode = fixedCode.replace(reg, `r.core.`);
      }
    }
    
    if (langStrings[finalCode] == null) {
      return code;
    }
    
    const templates = langStrings[finalCode].split('|');
    const template = properties(
      typeof templates[position] === 'string' ? templates[position] : templates[0],
      http.parse(realCode).query
    );
    
    if (!template.includes(LANG_TEMPLATE_VAR)) {
      return template;
    }
    
    return template
      .split(LANG_TEMPLATE_VAR)
      .reduce((result, fragment, index, source) => {
        let subst = vars[index] != null ? vars[index] : '';
        subst = index !== source.length - 1 ? subst : '';
        return `${result}${fragment}${subst}`;
      }, '');
  } catch (error) {
    console.error('[Service Worker] Error getting translation:', error);
    return code;
  }
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

const APP_VERSION = process.env.REACT_APP_VERSION || Date.now().toString();

export function register(config) {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

      if (isLocalhost) {
        // В development режиме не регистрируем Service Worker
        console.log('[Service Worker] Skipping registration in development');
        return;
      }

      registerValidSW(swUrl, config);
    });
  }
}

function registerValidSW(swUrl, config) {
  const hadController = !!navigator.serviceWorker.controller;
  
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('[Service Worker] Registered:', registration.scope);

      // Проверяем обновления каждые 60 секунд
      setInterval(() => {
        registration.update();
      }, 60000);

      // Обработка обновления Service Worker
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;

        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // Новый Service Worker доступен
                console.log('[Service Worker] New version available');
                
                // Уведомляем пользователя о доступности обновления
                if (config && config.onUpdate) {
                  config.onUpdate(registration);
                } else {
                  // По умолчанию - перезагружаем страницу
                  const getLangString = config?.getLangString || getLangStringFromStore;
                  const message = getLangString('r._.update.notification.confirm');
                  const finalMessage = message === 'r._.update.notification.confirm' 
                    ? 'Доступна новая версия приложения. Обновить?'
                    : message;
                  
                  if (confirm(finalMessage)) {
                    // Очищаем кеш языковых данных и HTML перед обновлением
                    api.clearLangCache();
                    api.clearHtmlCache();
                    
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                    window.location.reload();
                  }
                }
              } else {
                // Service Worker установлен впервые
                console.log('[Service Worker] Content cached for offline use');
              }
            }
          });
        }
      });

      // Обработка контролируемого Service Worker
      // Перезагружаем страницу только если это обновление, а не первая установка
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing && hadController) {
          refreshing = true;
          window.location.reload();
        }
      });
    })
    .catch((error) => {
      console.error('[Service Worker] Registration failed:', error);
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// Получить версию приложения из Service Worker
export function getAppVersion() {
  return new Promise((resolve) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
    } else {
      resolve({ version: APP_VERSION, buildTime: null });
    }
  });
}

// Очистить кеш
export function clearCache() {
  return new Promise((resolve) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [messageChannel.port2]
      );
    } else {
      resolve({ success: false });
    }
  });
}

// Проверить наличие обновлений
export async function checkForUpdates() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      
      // Проверяем версию
      const currentVersion = await getAppVersion();
      const htmlVersion = document.documentElement.getAttribute('data-version');
      
      if (currentVersion.version !== htmlVersion) {
        // Версии не совпадают - требуется обновление
        return true;
      }
    } catch (error) {
      console.error('[Service Worker] Error checking for updates:', error);
    }
  }
  return false;
}

